import React from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Truck, MapPin, Clock, Phone, ChevronLeft, ChevronRight, CalendarDays, CheckCircle2, Search, X, Navigation, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { toDateInputValue, startOfNzDay, endOfNzDay, addNzDays, parseNzWallToUtc } from '@/lib/format-date';
import { useConfirm } from '@/hooks/use-confirm';
import { toast } from '@/components/ui/toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { COMPLETE, nextStatusAction, statusBadgeClass } from './status';

interface Address {
  addressLine1?: string; addressLine2?: string; city?: string;
  stateProvince?: string; postalCode?: string; country?: string;
  latitude?: number | null; longitude?: number | null;
}

interface DispatchJob {
  id: string;
  jobNumber: string | null;
  customer: string | null;
  fleetNumber: string | null;
  jobDetails: string | null;
  info: string | null;
  start: string | null;
  end: string | null;
  phoneNumber: string | null;
  status: string | null;
  address: Address | null;
}

/** ISO `start_gte` / `start_lte` window spanning `d`'s **NZ** calendar day. We
 *  can't just send the bare date because the server stores `start` as a UTC
 *  Date and the CRUD filter casts string → Date. Bounds must match the depot's
 *  day, not the browser's, or a driver abroad (or on a device with a stale tz)
 *  gets a window straddling two of the depot's days. */
function dayBounds(d: Date): { gte: string; lte: string } {
  return { gte: startOfNzDay(d).toISOString(), lte: endOfNzDay(d).toISOString() };
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  // Force Pacific/Auckland so drivers see the depot's wall-clock regardless
  // of their browser tz.
  return new Date(iso).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false });
}

function fmtAddress(a: Address | null): string | null {
  if (!a) return null;
  const parts = [a.addressLine1, a.addressLine2, a.city, a.stateProvince, a.postalCode]
    .map((s) => (s ?? '').trim())
    .filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

/** Universal "open in maps" URL — works on both iOS (Apple Maps takes over
 *  this scheme) and Android (Google Maps opens it natively). */
function mapsUrl(a: Address): string {
  if (a.latitude != null && a.longitude != null) {
    return `https://www.google.com/maps/search/?api=1&query=${a.latitude},${a.longitude}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fmtAddress(a) ?? '')}`;
}

/** YYYY-MM-DD for `<input type="date">`, on the NZ calendar. toISOString()
 *  reports the UTC day (a day behind through an NZ morning); the runtime's
 *  local parts are wrong for offshore staff. `toDateInputValue` is the app's
 *  single source of truth for this — see lib/format-date.ts. */
const isoDateInput = toDateInputValue;

export function MyDispatchPage() {
  const employee = useAuthStore((s) => s.employee);
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [date, setDate] = React.useState<Date>(() => new Date());

  const { gte, lte } = dayBounds(date);

  const queryKey = ['/my-dispatch', employee?.id, gte, lte];
  const { data, isLoading, error } = useQuery<{ data: DispatchJob[] }>({
    queryKey,
    queryFn: async () => {
      const qs = new URLSearchParams({
        driver: employee!.id,
        start_gte: gte,
        start_lte: lte,
        limit: '100',
        sort: 'start',
        order: 'asc',
      });
      const res = await api<DispatchJob[]>(`/dispatch/jobs?${qs.toString()}`);
      return { data: res.data ?? [] };
    },
    enabled: !!employee?.id,
    // Silent background refresh so a dispatcher reassignment lands within ~a
    // minute without the driver having to reload. Paused while the tab is
    // hidden (the default) — no battery drain when the phone is locked.
    refetchInterval: 60_000,
    // Because the poll pauses in the background, catch up immediately when
    // the driver brings the app back to the foreground (app-wide default is
    // no focus refetch; this list is the driver's live worksheet).
    refetchOnWindowFocus: true,
  });

  const statusMutation = useMutation({
    mutationFn: async (vars: { jobId: string; status: string }) => {
      const res = await api(`/dispatch/jobs/${vars.jobId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: vars.status }),
      });
      if (!res.success) throw new Error(res.error?.message ?? 'Failed to update job');
      return res;
    },
    // Optimistic flip: stamp the card with the new status the instant the
    // user taps the action button, so the UI feels instant on flaky cellular.
    // The server response reconciles on settle.
    onMutate: async ({ jobId, status }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<{ data: DispatchJob[] }>(queryKey);
      if (previous) {
        queryClient.setQueryData<{ data: DispatchJob[] }>(queryKey, {
          data: previous.data.map((j) => (j.id === jobId ? { ...j, status } : j)),
        });
      }
      return { previous };
    },
    onError: (err: Error, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
      toast('Update failed', err.message, 'error');
      // Failure is the one case data-sync never reports — but on flaky
      // cellular the server may have applied the PUT even though the client
      // saw an error. Re-read truth instead of trusting the rollback.
      queryClient.invalidateQueries({ queryKey: ['/my-dispatch'] });
      queryClient.invalidateQueries({ queryKey: ['/dispatch/jobs', vars.jobId] });
    },
    onSuccess: (_res, vars) => {
      if (vars.status === COMPLETE) toast('Marked as complete', undefined, 'success');
    },
    // Cache refresh (list, detail, dispatcher board, other tabs) is handled
    // centrally by lib/data-sync.ts off the PUT; onError above already rolled
    // back the optimistic flip.
  });

  /** Advance a job to its next status. Only the terminal (Complete) hop asks
   *  for confirmation — En route / Arrived are low-stakes broadcasts a driver
   *  should be able to fire with a single tap from the car. */
  async function advanceStatus(job: DispatchJob) {
    const action = nextStatusAction(job.status);
    if (!action) return;
    if (action.terminal) {
      const ok = await confirm({
        title: 'Mark this job as complete?',
        description: job.customer ? `${job.customer} • ${fmtTime(job.start)}–${fmtTime(job.end)}` : undefined,
        confirmLabel: 'Mark Complete',
      });
      if (!ok) return;
    }
    statusMutation.mutate({ jobId: job.id, status: action.next });
  }

  const [searchInput, setSearchInput] = React.useState('');
  const search = searchInput.trim().toLowerCase();

  const allJobs = data?.data ?? [];
  // Client-side search across the fields a driver actually scans: customer
  // name, job number, fleet number, and the schedule-grid `info` line that
  // already concatenates customer + item codes.
  const jobs = !search
    ? allJobs
    : allJobs.filter((j) => {
        const hay = [j.customer, j.jobNumber, j.fleetNumber, j.info, j.address?.city]
          .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(search);
      });
  const open = jobs.filter((j) => j.status !== COMPLETE);
  const done = jobs.filter((j) => j.status === COMPLETE);

  // "Next up" — first open job (chronological, list already comes back sorted
  // ascending by start). Only surface for today's view; on other days the
  // banner would mislead a driver about what's actually next.
  const isToday = isoDateInput(date) === isoDateInput(new Date());
  const nextUp = isToday && !search ? allJobs.find((j) => j.status !== COMPLETE) ?? null : null;

  function shiftDay(delta: number) {
    setDate(addNzDays(date, delta));
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 pb-24">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Truck className="h-5 w-5 text-primary" /> My Dispatch
        </h1>
        <p className="text-sm text-muted-foreground">
          {employee?.fullName ? `Hi ${employee.fullName.split(' ')[0]} — ` : ''}
          your jobs for the selected day.
        </p>
      </div>

      {/* Date picker — large tap targets, prev/next arrows so a gloved hand
          can move days without opening the native date picker. */}
      <Card>
        <CardContent className="p-2 flex items-center gap-1 sm:gap-2">
          <Button variant="outline" size="icon" onClick={() => shiftDay(-1)} aria-label="Previous day" className="flex-shrink-0">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <label className="flex-1 min-w-0 flex items-center gap-1 sm:gap-2 px-2 py-2 rounded-md border bg-background min-h-12">
            <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <input
              type="date"
              value={isoDateInput(date)}
              onChange={(e) => {
                const picked = parseNzWallToUtc(`${e.target.value}T00:00`);
                if (picked) setDate(picked);
              }}
              className="flex-1 min-w-0 bg-transparent outline-none text-base"
            />
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => setDate(new Date())}
              className="text-xs flex-shrink-0 px-2"
            >
              Today
            </Button>
          </label>
          <Button variant="outline" size="icon" onClick={() => shiftDay(1)} aria-label="Next day" className="flex-shrink-0">
            <ChevronRight className="h-5 w-5" />
          </Button>
        </CardContent>
      </Card>

      {/* "Next up" banner — biggest at-a-glance value for a driver who just
          opened the app at the start of the shift. One tap → directions, two
          taps → job detail. Only renders for today's view with no active
          search (a filtered list shouldn't fake a "next" that isn't really
          chronological). */}
      {nextUp && (
        <Card
          className="border-primary/40 bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/my-dispatch/${nextUp.id}`)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/my-dispatch/${nextUp.id}`); } }}
        >
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-primary font-semibold">
              <Clock className="h-3.5 w-3.5" /> Next up · {fmtTime(nextUp.start)}
            </div>
            <div className="font-semibold text-base truncate">
              {nextUp.customer || nextUp.jobNumber || 'Untitled job'}
            </div>
            {fmtAddress(nextUp.address) && (
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                <span className="break-words">{fmtAddress(nextUp.address)}</span>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              {nextUp.address && fmtAddress(nextUp.address) && (
                <Button
                  size="sm"
                  className="flex-1 h-10"
                  onClick={(e) => { e.stopPropagation(); window.open(mapsUrl(nextUp.address!), '_blank', 'noopener,noreferrer'); }}
                >
                  <Navigation className="h-4 w-4 mr-1.5" /> Directions
                </Button>
              )}
              <Button
                variant="outline" size="sm" className="flex-1 h-10"
                onClick={(e) => { e.stopPropagation(); navigate(`/my-dispatch/${nextUp.id}`); }}
              >
                Open <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search — client-side filter across customer / job # / fleet # / info /
          city. Empty input shows everything. */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="search"
          placeholder="Search jobs (customer, job #, fleet #)"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-9 pr-9 h-11"
        />
        {searchInput && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => setSearchInput('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-muted"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-5 w-16" />
                </div>
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {error && (
        <Card><CardContent className="py-8 text-center text-sm text-destructive">
          Couldn't load your jobs. {(error as Error).message}
        </CardContent></Card>
      )}

      {!isLoading && !error && (
        <Tabs defaultValue="open" className="w-full">
          <TabsList className="grid grid-cols-2 w-full h-11">
            <TabsTrigger value="open" className="h-9">
              Open <span className="ml-1.5 text-xs opacity-70">({open.length})</span>
            </TabsTrigger>
            <TabsTrigger value="done" className="h-9">
              Done <span className="ml-1.5 text-xs opacity-70">({done.length})</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="open" className="space-y-3">
            {open.length === 0 ? (
              <EmptyState message={search ? 'No jobs match that search.' : 'No open jobs for this day.'} />
            ) : open.map((j) => (
              <JobCard
                key={j.id}
                job={j}
                onOpen={() => navigate(`/my-dispatch/${j.id}`)}
                onAdvance={advanceStatus}
                pending={statusMutation.isPending && statusMutation.variables?.jobId === j.id}
              />
            ))}
          </TabsContent>

          <TabsContent value="done" className="space-y-3">
            {done.length === 0 ? (
              <EmptyState message={search ? 'No completed jobs match that search.' : 'Nothing completed yet today.'} />
            ) : done.map((j) => (
              <JobCard
                key={j.id}
                job={j}
                onOpen={() => navigate(`/my-dispatch/${j.id}`)}
                onAdvance={advanceStatus}
                pending={false}
              />
            ))}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-10 text-center text-sm text-muted-foreground">{message}</CardContent>
    </Card>
  );
}

function JobCard({ job, onOpen, onAdvance, pending }: {
  job: DispatchJob;
  onOpen: () => void;
  onAdvance: (j: DispatchJob) => void;
  pending: boolean;
}) {
  const addr = fmtAddress(job.address);
  const isDone = job.status === COMPLETE;
  const action = nextStatusAction(job.status);

  // Stop the card's onClick from firing when a user taps an inner link or
  // button (maps, phone, status action). Without this every tap navigates to
  // detail and we never reach the deep action.
  const stop = (e: React.MouseEvent | React.KeyboardEvent) => e.stopPropagation();

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      className={`cursor-pointer transition-colors hover:bg-muted/40 active:bg-muted/60 ${isDone ? 'opacity-75' : ''}`}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-base truncate">
              {job.customer || job.jobNumber || 'Untitled job'}
            </div>
            {job.jobNumber && job.customer && (
              <div className="text-xs text-muted-foreground">#{job.jobNumber}</div>
            )}
          </div>
          <StatusBadge status={job.status} />
        </div>

        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>{fmtTime(job.start)} – {fmtTime(job.end)}</span>
          {job.fleetNumber && (
            <span className="ml-auto text-xs text-muted-foreground">Fleet #{job.fleetNumber}</span>
          )}
        </div>

        {addr && job.address && (
          <a
            href={mapsUrl(job.address)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stop}
            className="flex items-start gap-2 text-sm text-primary hover:underline"
          >
            <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
            <span className="break-words">{addr}</span>
          </a>
        )}

        {job.phoneNumber && (
          <a
            href={`tel:${job.phoneNumber}`}
            onClick={stop}
            className="flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <Phone className="h-4 w-4 shrink-0" />
            <span>{job.phoneNumber}</span>
          </a>
        )}

        {(job.jobDetails || job.info) && (
          <div className="text-sm whitespace-pre-wrap break-words text-foreground/80 line-clamp-3">
            {job.jobDetails || job.info}
          </div>
        )}

        {action && (
          <Button
            onClick={(e) => { stop(e); onAdvance(job); }}
            disabled={pending}
            className="w-full h-12 text-base"
            variant={action.terminal ? 'default' : 'default'}
          >
            {action.terminal
              ? <CheckCircle2 className="h-5 w-5 mr-2" />
              : <ArrowRight className="h-5 w-5 mr-2" />}
            {pending ? 'Updating…' : action.label}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge variant="secondary">No status</Badge>;
  if (status === COMPLETE) return <Badge variant="success">Complete</Badge>;
  const cls = statusBadgeClass(status);
  if (cls) return <Badge className={cls}>{status}</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}
